import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { SkillChip } from "@/components/prooftree/SkillChip"
import { Box } from "@/components/ui/box"
import { Button, ButtonText } from "@/components/ui/button"
import { HStack } from "@/components/ui/hstack"
import { Text } from "@/components/ui/text"
import { Textarea, TextareaInput } from "@/components/ui/textarea"
import { VStack } from "@/components/ui/vstack"
import { compactFilterChipStyle, filterChipStyle } from "@/constants/ChipStyles"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import {
  allSkills,
  CoachRecommendation,
  developmentJournal,
  getCoachRecommendations,
  getContact,
  getJournalEntriesForContact,
  getLocalDateKey,
  JournalEntry,
  Visibility,
} from "@/data/prooftree"
import {
  createEqGrowthCirclePost,
  createEqJournalEntry,
  listEqJournalEntries,
} from "@/services/eqParityBackend"
import { router, useLocalSearchParams } from "expo-router"
import {
  Award,
  CalendarDays,
  Globe2,
  Lock,
  PlayCircle,
  Search,
  Sparkles,
  UsersRound,
} from "lucide-react-native"
import { useEffect, useRef, useState } from "react"
import { Animated, Platform, ScrollView, TouchableOpacity, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const filters = ["All", "Private", "Group", "Video"]
const composerSkillIds = allSkills.slice(0, 6).map((skill) => skill.id)
const visibilityOptions: Visibility[] = ["private", "group", "public"]
const journalRecommendation = getCoachRecommendations("journal")[0]

function visibilityLabel(visibility: Visibility) {
  if (visibility === "group") return "Growth Circle"
  if (visibility === "public") return "Public"
  return "Private"
}

function VisibilityIcon({ visibility, color }: { visibility: Visibility; color: string }) {
  if (visibility === "group") return <UsersRound color={color} size={14} />
  if (visibility === "public") return <Globe2 color={color} size={14} />
  return <Lock color={color} size={14} />
}

export default function DevelopmentJournalScreen() {
  const auth = useAuth()
  const isSignedIn = Boolean(auth.session)
  const { contactId } = useLocalSearchParams<{ contactId?: string }>()
  const contact = isSignedIn ? undefined : getContact(contactId)
  const localJournal = isSignedIn
    ? []
    : contact
      ? getJournalEntriesForContact(contact.id)
      : developmentJournal
  const [activeFilter, setActiveFilter] = useState("All")
  const [activeSkillFilter, setActiveSkillFilter] = useState<string | null>(null)
  const [backendJournal, setBackendJournal] = useState<JournalEntry[]>(
    isSignedIn ? [] : localJournal,
  )
  const [localDraftEntries, setLocalDraftEntries] = useState<JournalEntry[]>([])
  const [draftReflection, setDraftReflection] = useState("")
  const [draftSkillId, setDraftSkillId] = useState(
    composerSkillIds[0] ?? "active_listening_presence",
  )
  const [draftVisibility, setDraftVisibility] = useState<Visibility>("private")
  const [journalMode, setJournalMode] = useState<"supabase" | "local">("local")
  const [journalMessage, setJournalMessage] = useState(
    isSignedIn
      ? "Loading your Supabase Journal entries."
      : "Using local fallback Journal entries until Supabase sync is available.",
  )
  const [sharingId, setSharingId] = useState<string | null>(null)
  const [savingReflection, setSavingReflection] = useState(false)
  const scrollY = useRef(new Animated.Value(0)).current
  const sourceJournal =
    journalMode === "supabase"
      ? backendJournal
      : isSignedIn
        ? []
        : [...localDraftEntries, ...localJournal]
  const skillFilters = Array.from(new Set(sourceJournal.flatMap((entry) => entry.skillIds)))
  const entries = sourceJournal.filter((entry) => {
    const matchesType =
      activeFilter === "Private"
        ? entry.visibility === "private"
        : activeFilter === "Group"
          ? entry.visibility === "group"
          : activeFilter === "Video"
            ? Boolean(entry.videoThumbnail)
            : true
    const matchesSkill = activeSkillFilter ? entry.skillIds.includes(activeSkillFilter) : true

    return matchesType && matchesSkill
  })

  useEffect(() => {
    let cancelled = false

    async function loadJournal() {
      const result = await listEqJournalEntries({ contactId })
      if (cancelled) return

      setJournalMode(result.mode)
      setJournalMessage(result.message)
      setBackendJournal(result.mode === "supabase" || isSignedIn ? result.items : [])
    }

    loadJournal()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, contactId, isSignedIn])

  async function shareEntry(entryId: string) {
    const entry = sourceJournal.find((candidate) => candidate.id === entryId)
    if (!entry || sharingId) return

    setSharingId(entry.id)
    const result = await createEqGrowthCirclePost({
      title: entry.taskTitle,
      summary: entry.text,
      journalEntryId: entry.id,
      visibility: "group",
    })
    setJournalMessage(result.message)
    setSharingId(null)
  }

  async function saveStandaloneReflection() {
    const reflectionText = draftReflection.trim()
    if (!reflectionText || savingReflection) return

    setSavingReflection(true)
    const result = await createEqJournalEntry({
      title: "Standalone reflection",
      reflectionText,
      skillIds: [draftSkillId],
      contactId,
      visibility: draftVisibility,
    })
    const entry: JournalEntry = {
      id: result.persisted && result.id ? result.id : `local-journal-${Date.now()}`,
      taskId: result.persisted && result.id ? result.id : `local-journal-task-${Date.now()}`,
      taskTitle: "Standalone reflection",
      date: getLocalDateKey(),
      skillIds: [draftSkillId],
      contactIds: contactId ? [contactId] : undefined,
      text: reflectionText,
      visibility: draftVisibility,
    }

    if (result.persisted) {
      setBackendJournal((current) => [entry, ...current])
      setJournalMode("supabase")
    } else if (!isSignedIn) {
      setLocalDraftEntries((current) => [entry, ...current])
    }
    setDraftReflection("")
    setJournalMessage(result.message)
    setSavingReflection(false)
  }

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow={contact ? "Filtered Journal" : "Development Journal"}
        title={contact ? `${contact.name} Timeline` : "Reflection Timeline"}
        subtitle={
          contact
            ? `Reflections and proof moments connected to ${contact.name}.`
            : "Text and video proof stay private unless you choose to share."
        }
        accentColor={Colors.dark.proof}
        backFallbackHref="/profile"
        statusText={journalMode === "supabase" ? "Synced" : "Local"}
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 20,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 132 : 116,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-5 rounded-3xl border p-4"
          style={{
            backgroundColor: `${Colors.dark.proof}14`,
            borderColor: `${Colors.dark.proof}3D`,
          }}
        >
          <HStack className="items-center gap-3">
            <Box
              className="h-11 w-11 items-center justify-center rounded-2xl"
              style={{ backgroundColor: `${Colors.dark.proof}24` }}
            >
              <Award color={Colors.dark.proof} size={20} />
            </Box>
            <VStack className="flex-1 gap-1">
              <Text className="font-bold text-white">Personal growth log</Text>
              <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
                {sourceJournal.length} {journalMode === "supabase" ? "Supabase" : "fallback"}{" "}
                reflections connected to tasks and EQ skills.
              </Text>
              <Text className="text-xs font-bold" style={{ color: Colors.dark.proof }}>
                {journalMessage}
              </Text>
            </VStack>
          </HStack>
        </Box>

        <JournalPromptCard recommendation={journalRecommendation} />

        <Box
          className="mb-5 rounded-3xl border p-4"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: `${Colors.dark.proof}44`,
          }}
        >
          <VStack className="gap-3">
            <VStack className="gap-1">
              <Text className="text-xs font-bold" style={{ color: Colors.dark.proof }}>
                QUICK REFLECTION
              </Text>
              <Text className="font-bold text-white">Add a standalone Journal entry</Text>
            </VStack>
            <Textarea
              size="lg"
              className="h-28 rounded-2xl border"
              style={{
                backgroundColor: Colors.dark.surface,
                borderColor: `${Colors.dark.proof}44`,
              }}
            >
              <TextareaInput
                value={draftReflection}
                onChangeText={setDraftReflection}
                className="p-4 text-white"
                placeholder="What did you notice, practice, or learn?"
                placeholderTextColor={Colors.dark.textSecondary}
                multiline
              />
            </Textarea>

            <HStack className="flex-wrap items-center gap-2">
              {composerSkillIds.map((skillId) => (
                <TouchableOpacity
                  key={skillId}
                  activeOpacity={0.86}
                  onPress={() => setDraftSkillId(skillId)}
                >
                  <SkillChip skillId={skillId} selected={draftSkillId === skillId} compact />
                </TouchableOpacity>
              ))}
            </HStack>

            <HStack className="flex-wrap items-center gap-2">
              {visibilityOptions.map((option) => {
                const active = draftVisibility === option
                return (
                  <TouchableOpacity
                    key={option}
                    activeOpacity={0.86}
                    onPress={() => setDraftVisibility(option)}
                  >
                    <Box
                      className="rounded-full border px-3 py-2"
                      style={{
                        ...compactFilterChipStyle,
                        backgroundColor: active ? `${Colors.dark.proof}29` : Colors.dark.surface,
                        borderColor: active ? Colors.dark.proof : "rgba(255,255,255,0.08)",
                      }}
                    >
                      <HStack className="items-center gap-2">
                        <VisibilityIcon
                          visibility={option}
                          color={active ? Colors.dark.proof : Colors.dark.textSecondary}
                        />
                        <Text
                          className="text-xs font-bold"
                          style={{ color: active ? Colors.dark.proof : Colors.dark.textSecondary }}
                        >
                          {visibilityLabel(option)}
                        </Text>
                      </HStack>
                    </Box>
                  </TouchableOpacity>
                )
              })}
            </HStack>

            <Button
              size="lg"
              className="rounded-2xl"
              disabled={savingReflection || !draftReflection.trim()}
              style={{ backgroundColor: Colors.dark.proof, opacity: savingReflection ? 0.72 : 1 }}
              onPress={saveStandaloneReflection}
            >
              <Award color={Colors.dark.primaryForeground} size={16} />
              <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
                {savingReflection ? "Saving..." : "Save Reflection"}
              </ButtonText>
            </Button>
          </VStack>
        </Box>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} className="-mx-5 mb-5 px-5">
          <HStack className="items-center gap-2">
            {filters.map((filter) => (
              <TouchableOpacity
                key={filter}
                activeOpacity={0.86}
                onPress={() => setActiveFilter(filter)}
              >
                <Box
                  className="rounded-full border px-4 py-2"
                  style={{
                    ...filterChipStyle,
                    backgroundColor:
                      activeFilter === filter
                        ? `${Colors.dark.proof}29`
                        : Colors.dark.surfaceSecondary,
                    borderColor:
                      activeFilter === filter ? Colors.dark.proof : "rgba(255,255,255,0.06)",
                  }}
                >
                  <Text
                    className="text-sm font-bold"
                    style={{
                      color:
                        activeFilter === filter ? Colors.dark.proof : Colors.dark.textSecondary,
                    }}
                  >
                    {filter}
                  </Text>
                </Box>
              </TouchableOpacity>
            ))}
          </HStack>
        </ScrollView>

        <ScrollView horizontal showsHorizontalScrollIndicator={false} className="-mx-5 mb-6 px-5">
          <HStack className="items-center gap-2">
            <TouchableOpacity activeOpacity={0.86} onPress={() => setActiveSkillFilter(null)}>
              <Box
                className="rounded-full border px-3 py-2"
                style={{
                  ...compactFilterChipStyle,
                  backgroundColor: activeSkillFilter
                    ? Colors.dark.surfaceSecondary
                    : `${Colors.dark.proof}29`,
                  borderColor: activeSkillFilter ? "rgba(255,255,255,0.06)" : Colors.dark.proof,
                }}
              >
                <Text
                  className="text-xs font-bold"
                  style={{
                    color: activeSkillFilter ? Colors.dark.textSecondary : Colors.dark.proof,
                  }}
                >
                  All skills
                </Text>
              </Box>
            </TouchableOpacity>
            {skillFilters.map((skillId) => (
              <TouchableOpacity
                key={skillId}
                activeOpacity={0.86}
                onPress={() =>
                  setActiveSkillFilter((current) => (current === skillId ? null : skillId))
                }
              >
                <SkillChip skillId={skillId} selected={activeSkillFilter === skillId} compact />
              </TouchableOpacity>
            ))}
          </HStack>
        </ScrollView>

        {entries.length > 0 ? (
          <VStack className="gap-5">
            {entries.map((entry, index) => {
              const color = Colors.dark.proof
              const [year, month, day] = entry.date.split("-")
              return (
                <PressableScale key={entry.id}>
                  <Box className="flex-row gap-4">
                    <VStack className="w-14 items-center">
                      <Text className="text-2xl font-bold text-white">{day}</Text>
                      <Text className="text-[11px] font-bold" style={{ color }}>
                        {month}/{year.slice(2)}
                      </Text>
                      <Box
                        className="mt-3 h-9 w-9 items-center justify-center rounded-full border"
                        style={{ backgroundColor: `${color}20`, borderColor: `${color}77` }}
                      >
                        <CalendarDays color={color} size={18} />
                      </Box>
                      {index < entries.length - 1 ? (
                        <View
                          className="mt-2 w-px flex-1"
                          style={{ minHeight: 132, backgroundColor: `${color}44` }}
                        />
                      ) : null}
                    </VStack>
                    <Box
                      className="flex-1 rounded-3xl border p-5"
                      style={{
                        backgroundColor: Colors.dark.surfaceSecondary,
                        borderColor: `${color}44`,
                      }}
                    >
                      <HStack className="mb-3 items-start justify-between gap-3">
                        <VStack className="flex-1 gap-1">
                          <Text className="text-lg font-bold text-white">{entry.taskTitle}</Text>
                        </VStack>
                        <Box
                          className="rounded-full px-3 py-1"
                          style={{ backgroundColor: `${color}20` }}
                        >
                          <HStack className="items-center gap-1.5">
                            <VisibilityIcon visibility={entry.visibility} color={color} />
                            <Text className="text-xs font-bold" style={{ color }}>
                              {visibilityLabel(entry.visibility)}
                            </Text>
                          </HStack>
                        </Box>
                      </HStack>

                      <Text
                        className="mb-4 text-sm leading-5"
                        style={{ color: Colors.dark.textSecondary }}
                      >
                        {entry.text}
                      </Text>

                      <HStack className="mb-4 flex-wrap items-center gap-2">
                        {entry.skillIds.map((skillId) => (
                          <SkillChip key={skillId} skillId={skillId} compact />
                        ))}
                      </HStack>

                      <TouchableOpacity
                        activeOpacity={0.86}
                        disabled={sharingId === entry.id}
                        onPress={() => shareEntry(entry.id)}
                      >
                        <Box
                          className="mb-4 rounded-2xl border px-4 py-3"
                          style={{
                            backgroundColor: `${Colors.dark.growth}17`,
                            borderColor: `${Colors.dark.growth}44`,
                            opacity: sharingId === entry.id ? 0.72 : 1,
                          }}
                        >
                          <HStack className="items-center justify-center gap-2">
                            <UsersRound color={Colors.dark.growth} size={16} />
                            <Text className="font-bold" style={{ color: Colors.dark.growth }}>
                              {sharingId === entry.id ? "Sharing..." : "Share to Growth Circle"}
                            </Text>
                          </HStack>
                        </Box>
                      </TouchableOpacity>

                      {entry.videoThumbnail ? (
                        <TouchableOpacity activeOpacity={0.86}>
                          <Box
                            className="rounded-2xl border p-3"
                            style={{
                              backgroundColor: `${color}14`,
                              borderColor: `${color}44`,
                            }}
                          >
                            <HStack className="items-center gap-3">
                              <Box
                                className="h-14 w-14 items-center justify-center rounded-xl"
                                style={{ backgroundColor: `${color}24` }}
                              >
                                <PlayCircle color={color} size={28} />
                              </Box>
                              <VStack className="flex-1">
                                <Text className="font-bold text-white">{entry.videoThumbnail}</Text>
                                <Text
                                  className="text-xs"
                                  style={{ color: Colors.dark.textSecondary }}
                                >
                                  Tap to preview. Upload playback is not connected yet.
                                </Text>
                              </VStack>
                            </HStack>
                          </Box>
                        </TouchableOpacity>
                      ) : null}
                    </Box>
                  </Box>
                </PressableScale>
              )
            })}
          </VStack>
        ) : (
          <EmptyState
            icon={Search}
            title="No reflections in this filter"
            description="Adjust the filter or complete a linked task to capture a new reflection."
          />
        )}

        <Button
          size="xl"
          className="mt-6 rounded-2xl"
          style={{ backgroundColor: Colors.dark.proof }}
          onPress={() => router.push("/proof/new")}
        >
          <Award color={Colors.dark.primaryForeground} size={18} />
          <ButtonText className="font-bold" style={{ color: Colors.dark.primaryForeground }}>
            Log Proof Entry
          </ButtonText>
        </Button>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function JournalPromptCard({
  recommendation,
}: {
  recommendation: CoachRecommendation | undefined
}) {
  if (!recommendation) return null

  return (
    <PressableScale onPress={() => router.push(recommendation.route as never)}>
      <Box
        className="mb-5 rounded-3xl border p-4"
        style={{
          backgroundColor: `${recommendation.accentColor}14`,
          borderColor: `${recommendation.accentColor}44`,
        }}
      >
        <HStack className="items-start gap-3">
          <Box
            className="h-11 w-11 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${recommendation.accentColor}22` }}
          >
            <Sparkles color={recommendation.accentColor} size={19} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color: recommendation.accentColor }}>
              SMART REFLECTION PROMPT
            </Text>
            <Text className="text-lg font-bold text-white">{recommendation.title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {recommendation.reason}
            </Text>
            <Text className="mt-1 text-sm font-bold" style={{ color: recommendation.accentColor }}>
              What happened? What did I change? What will I try next?
            </Text>
          </VStack>
        </HStack>
      </Box>
    </PressableScale>
  )
}
